import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import config from 'config/config'
import cookie from 'js-cookie'
import AppAPIResponse from 'services/modals/AppAPIResponse'
import ClassRooms from 'services/modals/ClassRooms'
import {
  ActivityFeedItem,
  FormativeAssessment,
  SummativeAssessment
} from 'types/assessments'
import { ContrastMode } from 'config/theme'
import { User } from 'app/reducer/authSlice'
import { StudentClassroomsResponse } from 'types/api'

export interface StudentClassroomAssessmentsResponse {
  summatives: SummativeAssessment[]
  formatives: FormativeAssessment[]
  lesson_practice_enabled: boolean
  activity_feed: ActivityFeedItem[]
  completed_formatives: FormativeAssessment[]
}

export interface StudentMenuResponse extends Pick<
  User,
  'first_name' | 'last_name' | 'avatar_background_color' | 'avatar_text_color'
> {
  id: number
}

export interface StudentAvatarColorOption {
  avatar_background_color: string
  avatar_text_color: string
}

const normalizeClassroomsResponse = (response: any) => {
  const filtered = [...(response?.data?.classrooms || [])]

  return {
    classrooms: filtered
  }
}

const normalizeClassroomAssessmentsResponse = (response: any) => {
  /**
   * Keep the classroom dashboard response aligned with the backend payload so
   * active and completed formative sections can render from their dedicated
   * arrays without frontend reclassification.
   */
  return {
    summatives: response?.data?.summatives || [],
    formatives: response?.data?.formatives || [],
    lesson_practice_enabled: response?.data?.lesson_practice_enabled || false,
    activity_feed: response?.data?.activity_feed || [],
    completed_formatives: response?.data?.completed_formatives || []
  }
}

const normalizeStudentMenuResponse = (response: any): StudentMenuResponse => {
  /**
   * The menu endpoint may wrap its payload in an additional data property;
   * unwrap that response before reading the backend avatar color fields.
   */
  const menuData = response?.data?.data || response?.data || response

  return {
    id: menuData?.id || 0,
    first_name: menuData?.first_name || '',
    last_name: menuData?.last_name || '',
    avatar_background_color: menuData?.avatar_background_color || '',
    avatar_text_color: menuData?.avatar_text_color || ''
  }
}

const normalizeStudentAvatarColorsResponse = (
  response: any
): StudentAvatarColorOption[] => {
  /**
   * The avatar color endpoint returns the exact background and text color
   * pairs that the student dropdown should render and submit back unchanged.
   */
  return (response?.data?.data || []).map(
    (colorOption: StudentAvatarColorOption) => {
      return {
        avatar_background_color: colorOption.avatar_background_color || '',
        avatar_text_color: colorOption.avatar_text_color || ''
      }
    }
  )
}

export const studentApi = createApi({
  reducerPath: 'studentApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${config.defaults.api_url}/api/v26`,
    prepareHeaders: (headers) => {
      headers.set('apiKey', config.defaults.api_key)

      const token = cookie.get('token')
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }

      return headers
    }
  }),
  endpoints: (builder) => ({
    getStudentMenu: builder.query<StudentMenuResponse, void>({
      query: () => ({
        url: '/student/menu',
        method: 'GET'
      }),
      transformResponse: (response: unknown) =>
        normalizeStudentMenuResponse(response)
    }),
    getStudentAvatarColors: builder.query<StudentAvatarColorOption[], void>({
      query: () => ({
        url: '/student/avatar-colors',
        method: 'GET'
      }),
      transformResponse: (response: unknown) =>
        normalizeStudentAvatarColorsResponse(response)
    }),
    getStudentClassrooms: builder.query<StudentClassroomsResponse, void>({
      query: () => ({
        url: '/student/me/classrooms',
        method: 'GET'
      }),
      transformResponse: (
        response:
          | AppAPIResponse<StudentClassroomsResponse>
          | StudentClassroomsResponse
          | ClassRooms
      ) => normalizeClassroomsResponse(response)
    }),
    getStudentClassroomAssessments: builder.query<
      StudentClassroomAssessmentsResponse,
      number
    >({
      query: (classroomId) => ({
        url: `/student/me/classrooms/assessments?classroom_id=${classroomId}`,
        method: 'GET'
      }),
      transformResponse: (response: unknown) =>
        normalizeClassroomAssessmentsResponse(response)
    }),
    dismissStudentNotification: builder.mutation<unknown, number>({
      query: (notificationId) => ({
        url: `/student/me/notifications/${notificationId}`,
        method: 'DELETE'
      })
    }),
    updateStudentContrastPreset: builder.mutation<
      unknown,
      { contrast_preset: Uppercase<ContrastMode> }
    >({
      query: (body) => ({
        url: '/student/contrast',
        method: 'PUT',
        body
      })
    }),
    updateStudentAvatarColors: builder.mutation<
      unknown,
      {
        avatar_background_color: string
        avatar_text_color: string
      }
    >({
      query: (body) => ({
        url: '/student/avatar-colors',
        method: 'PUT',
        body
      })
    })
  })
})

export const {
  useGetStudentMenuQuery,
  useGetStudentAvatarColorsQuery,
  useGetStudentClassroomsQuery,
  useLazyGetStudentClassroomsQuery,
  useGetStudentClassroomAssessmentsQuery,
  useDismissStudentNotificationMutation,
  useUpdateStudentContrastPresetMutation,
  useUpdateStudentAvatarColorsMutation
} = studentApi
